import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useCurrency } from '@/App';
import { Search, FileText, Filter, Printer, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

// --- Helpers ---

/**
 * Función para obtener el estilo de Badge basado en el estado de la factura.
 */
const getStatusVariant = (status) => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('pagada') || lowerStatus.includes('cobrada')) return 'bg-[#2E7D32] text-white hover:bg-[#2E7D32]/90';
    if (lowerStatus.includes('pendiente')) return 'bg-[#B26A00] text-white hover:bg-[#B26A00]/90';
    if (lowerStatus.includes('anulada') || lowerStatus.includes('cancelada')) return 'destructive';
    return 'secondary';
};

/**
 * Etiqueta legible de un canal de venta.
 */
const getChannelLabel = (channel) => (channel === 'online' ? 'Tienda Online' : 'Local');

// --- Componente Principal ---

export default function InvoicesPage() {
    const [invoices, setInvoices] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'Venta', 'Compra', 'all'
    const [filterChannel, setFilterChannel] = useState('all'); // 'local', 'online', 'all'
    const [isLoading, setIsLoading] = useState(true);
    const { formatPrice, formatUSD, formatPYG } = useCurrency();

    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    // Devoluciones
    const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
    const [returnItems, setReturnItems] = useState({}); // { productId: quantity }
    const [returnReason, setReturnReason] = useState('');
    const [isReturning, setIsReturning] = useState(false);

    useEffect(() => {
        fetchInvoices();
    }, []);

    const fetchInvoices = async () => {
        setIsLoading(true);
        try {
            // Endpoint asumido: /api/v1/invoices
            const response = await axios.get(`${API}/invoices/`);
            // La API devuelve { count, invoices: [...] }
            setInvoices(response.data.invoices || []);
        } catch (error) {
            toast.error('Error al cargar las facturas');
            console.error('Error fetching invoices:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewDetails = async (invoice) => {
        try {
            const response = await axios.get(`${API}/invoices/${invoice.id}/details?type=${invoice.document_type}`);
            setSelectedInvoice({ ...response.data, document_type: invoice.document_type });
            setIsDetailsOpen(true);
        } catch (error) {
            toast.error('Error al cargar los detalles');
            console.error(error);
        }
    };

    // --- Lógica de Deoluciones ---

    const handleOpenReturnDialog = () => {
        if (!selectedInvoice) return;
        // Inicializar estado de items
        const initialItems = {};
        // Solo items seleccionables
        setReturnItems({});
        setReturnReason('');
        setIsReturnDialogOpen(true);
    };

    const handleReturnItemChange = (productId, isChecked) => {
        setReturnItems(prev => {
            const newItems = { ...prev };
            if (isChecked) {
                newItems[productId] = 1; // Default to 1
            } else {
                delete newItems[productId];
            }
            return newItems;
        });
    };

    const handleReturnQuantityChange = (productId, quantity, maxQuantity) => {
        const qty = parseInt(quantity);
        if (isNaN(qty) || qty < 1) return;
        // La validación de máximo ahora debe considerar quantity_returned, 
        // pero maxQuantity ya vendrá ajustado desde el renderizado (available)
        if (qty > maxQuantity) {
            toast.error(`La cantidad no puede exceder lo disponible (${maxQuantity})`);
            return;
        }
        setReturnItems(prev => ({
            ...prev,
            [productId]: qty
        }));
    };

    const handleSubmitReturn = async () => {
        if (!returnReason.trim()) {
            toast.error('Debe ingresar un motivo para la devolución');
            return;
        }

        const itemsToReturn = Object.entries(returnItems).map(([productId, qty]) => {
            const originalItem = selectedInvoice.items.find(i => (i.product_id || i.id) === productId);
            return {
                product_id: productId,
                product_name: originalItem.product_name || originalItem.name,
                quantity: qty,
                price_pyg: originalItem.price_pyg || 0,
                refund_pyg: 0 // Backend calculates this
            };
        });

        if (itemsToReturn.length === 0) {
            toast.error('Seleccione al menos un producto para devolver');
            return;
        }

        setIsReturning(true);
        try {
            await axios.post(`${API}/returns/`, {
                sale_id: selectedInvoice.id,
                items: itemsToReturn,
                reason: returnReason
            });

            toast.success('Devolución procesada exitosamente');
            setIsReturnDialogOpen(false);
            setIsDetailsOpen(false); // Close details as well to refresh context if needed
            fetchInvoices(); // Refresh list to update status if applicable
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.detail || 'Error al procesar devolución');
        } finally {
            setIsReturning(false);
        }
    };

    /**
     * Filtra y busca las facturas según el tipo de documento y la consulta de búsqueda.
     */
    const filteredInvoices = useMemo(() => {
        return invoices
            .filter(invoice => {
                // 1. Filtrar por tipo de documento
                if (filterType !== 'all' && invoice.document_type !== filterType) {
                    return false;
                }
                // 1.1 Filtrar por canal de venta (solo aplica a ventas)
                if (filterChannel !== 'all') {
                    const channel = invoice.document_type === 'Venta' ? (invoice.sales_channel || 'local') : null;
                    if (channel !== filterChannel) {
                        return false;
                    }
                }
                // 2. Filtrar por consulta de búsqueda (número o nombre de entidad)
                const query = searchQuery.toLowerCase();
                return invoice.invoice_number?.toLowerCase().includes(query) ||
                    invoice.entity_name?.toLowerCase().includes(query);
            });
    }, [searchQuery, filterType, filterChannel, invoices]);

    // Función para formatear fechas
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Cargando facturas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold flex items-center gap-2" data-font="display">
                <FileText className="size-6 text-muted-foreground" /> Gestión de Facturas
            </h1>

            <Card>
                <CardHeader>
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 flex-wrap">
                        {/* Búsqueda */}
                        <div className="flex items-center gap-2 w-full lg:w-2/5">
                            <Search className="size-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por N° Factura o Nombre de Entidad..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full"
                            />
                        </div>

                        {/* Filtro por Tipo */}
                        <div className="flex items-center gap-2 w-full sm:w-[180px]">
                            <Filter className="size-4 text-muted-foreground" />
                            <Select value={filterType} onValueChange={setFilterType}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Filtrar por tipo" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los Documentos</SelectItem>
                                    <SelectItem value="Venta">Facturas de Venta</SelectItem>
                                    <SelectItem value="Compra">Facturas de Compra</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Filtro por Canal */}
                        <div className="flex items-center gap-2 w-full sm:w-[180px]">
                            <Filter className="size-4 text-muted-foreground" />
                            <Select value={filterChannel} onValueChange={setFilterChannel}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Filtrar por canal" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los Canales</SelectItem>
                                    <SelectItem value="local">Local</SelectItem>
                                    <SelectItem value="online">Tienda Online</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[100px]">N° Factura</TableHead>
                                    <TableHead className="w-[120px]">Tipo</TableHead>
                                    <TableHead>Entidad (Cliente/Proveedor)</TableHead>
                                    <TableHead className="w-[120px]">Canal</TableHead>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead className="text-right">Monto Total</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredInvoices.map((invoice) => (
                                    <TableRow key={invoice.id}>
                                        <TableCell className="font-medium">{invoice.invoice_number || invoice.id}</TableCell>
                                        <TableCell>
                                            <Badge variant={invoice.document_type === 'Venta' ? 'default' : 'secondary'}>
                                                {invoice.document_type}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{invoice.entity_name}</TableCell>
                                        <TableCell>
                                            {invoice.document_type === 'Venta' ? (
                                                <Badge variant={getChannelLabel(invoice.sales_channel) === 'Tienda Online' ? 'default' : 'secondary'}>
                                                    {getChannelLabel(invoice.sales_channel)}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell>{formatDate(invoice.date)}</TableCell>
                                        <TableCell className="text-right font-semibold">
                                            {formatPYG(invoice.total_amount)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={getStatusVariant(invoice.status)}>
                                                {invoice.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleViewDetails(invoice)}
                                            >
                                                Ver Detalle
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredInvoices.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                            No se encontraron facturas con los filtros aplicados.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Modal de Detalles */}
            <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Detalles del Documento</DialogTitle>
                    </DialogHeader>
                    {selectedInvoice && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="font-semibold">Tipo:</p>
                                    <p>{selectedInvoice.document_type}</p>
                                </div>
                                <div>
                                    <p className="font-semibold">Fecha:</p>
                                    <p>{formatDate(selectedInvoice.created_at || selectedInvoice.date || selectedInvoice.order_date)}</p>
                                </div>
                                <div>
                                    <p className="font-semibold">Entidad:</p>
                                    <p>{selectedInvoice.client_name || selectedInvoice.supplier_name}</p>
                                </div>
                                {selectedInvoice.document_type === 'Venta' && (
                                    <div>
                                        <p className="font-semibold">Canal:</p>
                                        <Badge variant={getChannelLabel(selectedInvoice.sales_channel) === 'Tienda Online' ? 'default' : 'secondary'}>
                                            {getChannelLabel(selectedInvoice.sales_channel)}
                                        </Badge>
                                    </div>
                                )}
                                <div>
                                    <p className="font-semibold">Estado:</p>
                                    <Badge className={getStatusVariant(selectedInvoice.status || 'Pendiente')}>
                                        {selectedInvoice.status || 'Pendiente'}
                                    </Badge>
                                </div>
                                {selectedInvoice.invoice_details?.invoice_number && (
                                    <div>
                                        <p className="font-semibold">N° Factura:</p>
                                        <p>{selectedInvoice.invoice_details.invoice_number}</p>
                                    </div>
                                )}
                            </div>

                            <div className="border rounded-md p-4">
                                <h3 className="font-semibold mb-2">Ítems</h3>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Producto</TableHead>
                                            <TableHead className="text-right">Cantidad</TableHead>
                                            <TableHead className="text-right">Precio Unit.</TableHead>
                                            <TableHead className="text-right">Total</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {selectedInvoice.items?.map((item, index) => {
                                            const returned = item.quantity_returned || 0;
                                            return (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        {item.product_name || item.name || 'Producto'}
                                                        {returned > 0 && (
                                                            <span className="block text-xs text-orange-600 font-medium">
                                                                (Devueltos: {returned})
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right">{item.quantity}</TableCell>
                                                    <TableCell className="text-right">
                                                        {selectedInvoice.document_type === 'Venta'
                                                            ? formatPYG(item.price_pyg)
                                                            : formatPYG(item.cost_pyg)
                                                        }
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {selectedInvoice.document_type === 'Venta'
                                                            ? formatPYG(item.total_item_pyg)
                                                            : formatPYG(item.quantity * item.cost_pyg)
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </div>

                            <div className="flex justify-end space-x-4">
                                <div className="text-right space-y-1">
                                    {selectedInvoice.document_type === 'Venta' && (
                                        <>
                                            <p className="text-sm text-muted-foreground">
                                                Subtotal: <span className="font-medium">{formatPYG(selectedInvoice.subtotal_pyg)}</span>
                                            </p>
                                            {selectedInvoice.coupon_discount_pyg > 0 && (
                                                <p className="text-sm text-green-600">
                                                    Cupón{selectedInvoice.coupon_code ? ` (${selectedInvoice.coupon_code})` : ''}: -{formatPYG(selectedInvoice.coupon_discount_pyg)}
                                                </p>
                                            )}
                                            {selectedInvoice.delivery_fee_pyg > 0 && (
                                                <p className="text-sm text-muted-foreground">
                                                    Delivery: <span className="font-medium">{formatPYG(selectedInvoice.delivery_fee_pyg)}</span>
                                                </p>
                                            )}
                                            {selectedInvoice.iva_pyg > 0 && (
                                                <p className="text-sm text-muted-foreground">
                                                    IVA{selectedInvoice.iva_rate ? ` (${(selectedInvoice.iva_rate * 100).toFixed(0)}%)` : ''}: <span className="font-medium">{formatPYG(selectedInvoice.iva_pyg)}</span>
                                                </p>
                                            )}
                                        </>
                                    )}
                                    <p className="text-sm text-muted-foreground">Total</p>
                                    <p className="text-xl font-bold">
                                        {selectedInvoice.document_type === 'Venta'
                                            ? formatPYG(selectedInvoice.total_pyg)
                                            : formatPYG(selectedInvoice.total_cost_pyg)
                                        }
                                    </p>
                                </div>
                            </div>

                            <DialogFooter>
                                {selectedInvoice.document_type === 'Venta' && selectedInvoice.invoice_details?.id && (
                                    <Button onClick={() => window.open(`${API}/invoices/${selectedInvoice.invoice_details.id}/print`, '_blank')}>
                                        <Printer className="mr-2 h-4 w-4" /> Imprimir Factura
                                    </Button>
                                )}
                                {selectedInvoice.document_type === 'Venta' && (
                                    <Button variant="destructive" onClick={handleOpenReturnDialog}>
                                        <RotateCcw className="mr-2 h-4 w-4" /> Devolver Productos
                                    </Button>
                                )}
                                <Button variant="secondary" onClick={() => setIsDetailsOpen(false)}>Cerrar</Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal de Devolución */}
            <Dialog open={isReturnDialogOpen} onOpenChange={setIsReturnDialogOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>Devolución de Productos</DialogTitle>
                        <DialogDescription>
                            Seleccione los productos y cantidades a devolver. Esta acción revertirá stock y generará un registro.
                        </DialogDescription>
                    </DialogHeader>

                    {selectedInvoice && (
                        <div className="space-y-4">
                            <div className="border rounded-md p-4 max-h-[300px] overflow-y-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[50px]"></TableHead>
                                            <TableHead>Producto</TableHead>
                                            <TableHead className="text-right">Comprado</TableHead>
                                            <TableHead className="text-right w-[100px]">A Devolver</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {selectedInvoice.items?.map((item, index) => {
                                            const productId = item.product_id || item.id;
                                            const isSelected = !!returnItems[productId];
                                            const returned = item.quantity_returned || 0;
                                            const available = item.quantity - returned;

                                            return (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onCheckedChange={(checked) => handleReturnItemChange(productId, checked)}
                                                            disabled={available <= 0}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.product_name || item.name || 'Producto'}
                                                        {returned > 0 && (
                                                            <span className="ml-2 text-xs text-muted-foreground">
                                                                (Prev. devuelto: {returned})
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {item.quantity}
                                                        {returned > 0 && (
                                                            <span className="text-xs text-gray-500 block">Disp: {available}</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            max={available}
                                                            disabled={!isSelected}
                                                            value={returnItems[productId] || ''}
                                                            onChange={(e) => handleReturnQuantityChange(productId, e.target.value, available)}
                                                            className="h-8 text-right"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="reason">Motivo de Devolución *</Label>
                                <Textarea
                                    id="reason"
                                    placeholder="Explique el motivo (ej: producto dañado, error en pedido...)"
                                    value={returnReason}
                                    onChange={(e) => setReturnReason(e.target.value)}
                                />
                            </div>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsReturnDialogOpen(false)}>Cancelar</Button>
                                <Button
                                    variant="destructive"
                                    onClick={handleSubmitReturn}
                                    disabled={isReturning || Object.keys(returnItems).length === 0 || !returnReason}
                                >
                                    {isReturning ? 'Procesando...' : 'Confirmar Devolución'}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
